import React, { useState, useEffect } from 'react'
import { useFence } from '../contexts/FenceContext'

// 🚀 DEBUG FLAG - ustaw na true tylko do debugowania
const DEBUG_LOGS = false

interface ElementToolbarProps {
  onElementSelect: (id: string | null) => void
  selectedFence?: any // Dodajemy props dla wybranego ogrodzenia
  fullFenceConfig?: any // Pełna konfiguracja z JSON
}

const ElementToolbar: React.FC<ElementToolbarProps> = ({ onElementSelect, selectedFence, fullFenceConfig }) => {
  const { state, addPost, addGate, addDoor, addSection, updateElement, closePerimeter, recalculateAllPrices, selectVariant } = useFence()
  
  // 🎯 DEBUG: Sprawdź dostępność funkcji z kontekstu
  // console.log('🔍 ElementToolbar: Funkcje z kontekstu:', { // WYŁĄCZONE - powoduje spam logów
  //   resetProject: typeof resetProject,
  //   addPost: typeof addPost,
  //   state: state ? 'dostępny' : 'brak',
  //   elementsCount: state?.elements?.length || 0
  // })
  const [sectionWidth, setSectionWidth] = useState(0.32)
  const [isAddingSection] = useState(false)
  const [postHeight, setPostHeight] = useState(6) // 🎯 Wysokość słupka (domyślnie 6 bloków)
  
  // 🎯 Wybrany wariant - domyślnie pierwszy z listy dostępnych wariantów
  const defaultVariant = (fullFenceConfig?.availableVariants?.[0] || selectedFence?.availableVariants?.[0] || 'p22')
  const selectedVariant = state.selectedVariant || defaultVariant
  const [isLoadingFirstElement, setIsLoadingFirstElement] = useState(false) // 🚀 Stan ładowania pierwszego elementu
  
  // 🎯 Sprawdź ostatni element, żeby wyłączyć nieprawidłowe przyciski
  const lastElement = state.elements.length > 0 ? state.elements[state.elements.length - 1] : null
  const lastElementType = lastElement?.type
  
  // Wyłącz przyciski na podstawie ostatniego elementu:
  // - Jeśli ostatni to 'section', wyłącz 'gate', 'door' i 'section'
  // - Jeśli ostatni to 'gate' lub 'door', wyłącz 'section'
  // - 🚫 BLOKADA: Wyłącz wszystkie przyciski jeśli obwód jest zamknięty
  const isSectionDisabled = (lastElementType === 'section' || lastElementType === 'gate' || lastElementType === 'door') || !selectedFence || state.isClosed
  const isGateDisabled = lastElementType === 'section' || !selectedFence || state.isClosed
  const isDoorDisabled = lastElementType === 'section' || !selectedFence || state.isClosed
  const isPostDisabled = !selectedFence || isLoadingFirstElement || state.isClosed
  
  // 🚫 DEBUG: Sprawdź stan blokady
  if (state.isClosed) {
    DEBUG_LOGS && console.log('🚫 ElementToolbar: Obwód jest zamknięty - wszystkie przyciski powinny być zablokowane')
    DEBUG_LOGS && console.log('🚫 ElementToolbar: isPostDisabled=', isPostDisabled, 'isSectionDisabled=', isSectionDisabled, 'isGateDisabled=', isGateDisabled, 'isDoorDisabled=', isDoorDisabled)
  }
  
  // 🎯 TYMCZASOWE ROZWIĄZANIE: Ustaw postHeight na 5 dla Vision-Bet Long
  useEffect(() => {
    DEBUG_LOGS && console.log(`🔍 TYMCZASOWE DEBUG: state.selectedSystem=`, state.selectedSystem)
    DEBUG_LOGS && console.log(`🔍 TYMCZASOWE DEBUG: state.selectedSystem?.id=`, state.selectedSystem?.id)
    DEBUG_LOGS && console.log(`🔍 TYMCZASOWE DEBUG: state.selectedSystem?.name=`, state.selectedSystem?.name)
    
    if (state.selectedSystem?.id === 'vision-bet-long') {
      setPostHeight(5)
      DEBUG_LOGS && console.log(`🎯 TYMCZASOWE: Ustawiono postHeight na 5 dla Vision-Bet Long`)
    } else {
      DEBUG_LOGS && console.log(`⚠️ TYMCZASOWE: Nie ustawiono postHeight - selectedSystem.id=${state.selectedSystem?.id}`)
    }
  }, [state.selectedSystem])

  // 🎯 Aktualizuj postHeight gdy zmienia się fullFenceConfig, selectedFence lub selectedVariant
  useEffect(() => {
    const loadConfig = async () => {
      let config = fullFenceConfig || selectedFence
      
      // 🎯 Dla wariantów (Royal Mix / Muro Mix): Załaduj konfigurację wariantu
      if (state.selectedVariant && config?.variantConfigs?.[state.selectedVariant]) {
        const variantConfigId = config.variantConfigs[state.selectedVariant]
        const { getFenceConfigWithColor } = await import('../config/fenceConfigs')
        const variantConfig = await getFenceConfigWithColor(variantConfigId, state.selectedColor || undefined)
        if (variantConfig) {
          config = variantConfig
          DEBUG_LOGS && console.log(`🎯 ElementToolbar: Używam konfiguracji wariantu ${state.selectedVariant} -> ${variantConfigId} dla postHeight`)
        }
      }
      
      DEBUG_LOGS && console.log(`🔍 ElementToolbar useEffect: fullFenceConfig=`, fullFenceConfig)
      DEBUG_LOGS && console.log(`🔍 ElementToolbar useEffect: selectedFence=`, selectedFence)
      DEBUG_LOGS && console.log(`🔍 ElementToolbar useEffect: config=`, config)
      DEBUG_LOGS && console.log(`🔍 ElementToolbar useEffect: config?.elements?.post?.defaultHeight=`, config?.elements?.post?.defaultHeight)
      
      if (config?.elements?.post?.defaultHeight) {
        // 🎯 Suwak pokazuje tylko zwykłe bloki (bez dekoracyjnego)
        // defaultHeight to całkowita wysokość (zwykłe + dekoracyjne)
        const decorationBlocksCount = config?.elements?.post?.decorationBlocksCount || 1
        const regularBlocks = config.elements.post.defaultHeight - decorationBlocksCount
        setPostHeight(regularBlocks)
        DEBUG_LOGS && console.log(`🎯 ElementToolbar: Zaktualizowano postHeight na ${regularBlocks} (zwykłe bloki) dla ${config.name} (defaultHeight: ${config.elements.post.defaultHeight}, dekoracyjne: ${decorationBlocksCount})`)
      } else {
        DEBUG_LOGS && console.log(`⚠️ ElementToolbar: Brak defaultHeight w konfiguracji, zostawiam postHeight=${postHeight}`)
      }
    }
    
    loadConfig()
  }, [fullFenceConfig, selectedFence, state.selectedVariant, state.selectedColor])

  // 🎯 Sprawdź czy to ogrodzenie z wariantami (Muro Full, Royal Mix, itp.)
  const isFullFence = (fullFenceConfig?.availableVariants?.length ?? 0) > 0 || (selectedFence?.availableVariants?.length ?? 0) > 0
  const availableVariants = fullFenceConfig?.availableVariants || selectedFence?.availableVariants || []
  
  // 🎯 Aktualizuj selectedVariant gdy zmieni się ogrodzenie
  useEffect(() => {
    if (availableVariants.length > 0 && (!state.selectedVariant || !availableVariants.includes(state.selectedVariant))) {
      selectVariant(availableVariants[0])
      DEBUG_LOGS && console.log(`🎯 Ustawiono domyślny wariant: ${availableVariants[0]} dla ${fullFenceConfig?.name || selectedFence?.name}`)
    }
  }, [fullFenceConfig?.id, selectedFence?.id, availableVariants, state.selectedVariant, selectVariant])

  // 🎯 DEBUG: Sprawdź czy komponent się re-renderuje
  useEffect(() => {
    // DEBUG_LOGS && console.log(`🔄 ElementToolbar: Komponent się re-renderuje`) // WYŁĄCZONE - powoduje spam logów
    // DEBUG_LOGS && console.log(`🔍 ElementToolbar: Stack trace:`, new Error().stack) // WYŁĄCZONE - powoduje spam logów
    
    return () => {
      // DEBUG_LOGS && console.log(`🔚 ElementToolbar: Komponent się odmontowuje`) // WYŁĄCZONE - powoduje spam logów
    }
  })

  const getNextPosition = (elementType: string) => {
    console.log('🔍 DEBUG: getNextPosition wywołane dla typu:', elementType)
    console.log('🔍 DEBUG: Liczba elementów w state:', state.elements.length)
    
    // 🎯 WYŁĄCZONE: Używanie funkcji z 3D komponentu - powoduje błędne pozycjonowanie
    // if ((window as any).getNextElementPosition && selectedFence) {
    //   console.log('🎯 Używam funkcji z 3D komponentu')
    //   const result = (window as any).getNextElementPosition(selectedFence, state.elements)
    //   
    //   // 🎯 NOWE: Sprawdź czy otrzymano obiekt z pozycją i rotacją
    //   if (result && typeof result === 'object' && 'position' in result) {
    //     console.log('🎯 getNextPosition: Otrzymano pozycję i rotację z 3D:', result)
    //     return result.position
    //   } else {
    //     // Fallback: stara wersja która zwracała tylko pozycję
    //     console.log('🎯 getNextPosition: Otrzymano starą wersję (tylko pozycja):', result)
    //     return result
    //   }
    // }
    
    // Fallback: Oblicz następną pozycję na podstawie istniejących elementów 2D
    if (state.elements.length === 0) {
      // Jeśli punkt startu został wybrany w 3D – użyj go
      const startPoint = typeof window !== 'undefined' ? (window as any).__fenceStartPoint as { x: number; y: number; z: number } | undefined : undefined
      if (startPoint) {
        console.log('📍 Pierwszy element - start od wybranego punktu:', startPoint)
        return startPoint
      }

      console.log('📍 Pierwszy element - start od pozycji domyślnej:', { x: 0, y: 0, z: 0 })
      return { x: 0, y: 0, z: 0 } // Fallback: środek planszy
    }
    
    // DEBUG: Pokaż wszystkie elementy i ich wymiary
    console.log('🔍 DEBUG: Wszystkie elementy w state:', state.elements.map(el => ({
      id: el.id,
      type: el.type,
      position: el.position,
      width: el.width,
      height: el.height,
      rotation: el.rotation
    })))
    
    // Znajdź najdalej wysunięty element w prawo
    const rightmostElement = state.elements.reduce((rightmost, element) => {
      // 🎯 UŻYJ STAŁEJ SZEROKOŚCI DLA SŁUPKÓW W JEDNOSTKACH 2D
      let elementWidth = element.width
      if (element.type === 'post') {
        elementWidth = 0.16 // Słupki: 0.16 jednostki (spacing z konfiguracji)
      }
      
      let rightmostWidth = rightmost.width
      if (rightmost.type === 'post') {
        rightmostWidth = 0.16 // Słupki: 0.16 jednostki (spacing z konfiguracji)
      }
      
      // Uwzględnij rzeczywistą pozycję elementu + jego szerokość
      const elementRightEdge = element.position.x + elementWidth
      const rightmostRightEdge = rightmost.position.x + rightmostWidth
      
      DEBUG_LOGS && console.log(`🔍 DEBUG: Porównuję elementy:`, {
        element: { id: element.id, position: element.position.x, width: elementWidth, rightEdge: elementRightEdge },
        rightmost: { id: rightmost.id, position: rightmost.position.x, width: rightmostWidth, rightEdge: rightmostRightEdge }
      })
      
      return elementRightEdge > rightmostRightEdge ? element : rightmost
    })
    
    // Dodaj nowy element dokładnie na prawej krawędzi poprzedniego
    // 🎯 UŻYJ RÓŻNYCH SZEROKOŚCI DLA RÓŻNYCH TYPÓW ELEMENTÓW W JEDNOSTKACH 2D
    let rightmostWidth = rightmostElement.width
    if (rightmostElement.type === 'post') {
      rightmostWidth = 0.16 // Słupki: 0.16 jednostki (spacing z konfiguracji)
    } else if (rightmostElement.type === 'section') {
      rightmostWidth = 0.16 // Przęsła: 0.16 jednostek (spacing z konfiguracji)
    } else if (rightmostElement.type === 'gate') {
      rightmostWidth = 0.32 // Bramy: 0.32 jednostek (2x spacing)
    } else if (rightmostElement.type === 'door') {
      rightmostWidth = 0.16 // Drzwi: 0.16 jednostek (spacing z konfiguracji)
    } else {
      rightmostWidth = 0.16 // Domyślne: 0.16 jednostki (spacing z konfiguracji)
    }
    
    // 🎯 NAPRAWKA: POZYCJONOWANIE - uwzględnij rotację poprzedniego elementu
    // Kolejny słupek musi się od razu pojawiać pod tym samym kątem co poprzedni i od razu za nim
    
    // 2. Oblicz pozycję nowego elementu: uwzględnij rotację poprzedniego
    let newX, newZ
    
    if (Math.abs(rightmostElement.rotation) > 0.1) {
      // Element jest obrócony - ale kolejny element kontynuuje prostą linię wzdłuż osi X
      const spacing = 0.20 // standardowy spacing między elementami
      
      // Kolejny element kontynuuje prostą linię wzdłuż osi X (bez przesunięcia w Z)
      newX = rightmostElement.position.x + spacing
      newZ = rightmostElement.position.z // zachowaj tę samą pozycję Z
      
      DEBUG_LOGS && console.log(`🎯 getNextPosition (fallback): Element obrócony ${rightmostElement.rotation}° - kontynuuję prostą linię wzdłuż osi X:`, {
        spacing,
        previousPosition: { x: rightmostElement.position.x.toFixed(3), z: rightmostElement.position.z.toFixed(3) },
        newPosition: { x: newX.toFixed(3), z: newZ.toFixed(3) }
      })
    } else {
      // Element nie obrócony - dodaj stykające się w linii prostej
      const previousElementWidth = rightmostWidth // Użyj obliczonej szerokości
      const previousElementRightEdge = rightmostElement.position.x + previousElementWidth
      
      // Nowy element zaczyna się od prawej krawędzi poprzedniego
      newX = previousElementRightEdge
      newZ = rightmostElement.position.z || 0
      
      DEBUG_LOGS && console.log(`🎯 getNextPosition (fallback): Element nie obrócony - dodaję stykające się (edge-to-edge):`, {
        previousElementWidth,
        previousElementRightEdge: previousElementRightEdge.toFixed(3),
        newPosition: { x: newX.toFixed(3), z: newZ.toFixed(3) }
      })
    }
    
    console.log('🎯 DEBUG: GOTOWE - nowy element umieszczony (fallback):', {
      rightmostElement: { id: rightmostElement.id, position: rightmostElement.position.x, width: rightmostWidth, rotation: rightmostElement.rotation },
      newPosition: { x: newX, y: 0, z: newZ },
      algorithm: 'NAPRAWIONE - pozycjonowanie uwzględniające rotację (fallback)'
    })
    
    return { 
      x: newX, // Uwzględnia rotację
      y: 0, 
      z: newZ // Uwzględnia rotację
    }
  }


  const handleAddPost = async () => {
    console.log('🚀🚀🚀 NOWA WERSJA - OPTYMALIZACJA ŁADOWANIA - handleAddPost START 🚀🚀🚀')
    console.log('🚀 handleAddPost: START - funkcja wywołana!')
    console.log('🚀 handleAddPost: fullFenceConfig:', !!fullFenceConfig)
    console.log('🚀 handleAddPost: selectedFence:', !!selectedFence)
    
    // 🚫 BLOKADA: Nie można dodawać elementów po zamknięciu obwodu
    if (state.isClosed) {
      console.log('❌ handleAddPost: Obwód jest zamknięty - nie można dodawać elementów!')
      alert('Nie można dodawać elementów po zamknięciu obwodu!')
      return
    }
    
    // 🎯 FIX: Używaj tylko fullFenceConfig (FenceConfig), nie selectedFence (FenceSystem)
    if (!fullFenceConfig) {
      console.log('❌ handleAddPost: fullFenceConfig jest null!')
      alert('Najpierw wybierz system ogrodzenia i poczekaj na załadowanie konfiguracji!')
      return
    }
    
    // 🚀 BLOKADA: Jeśli to pierwszy element i już się ładuje, zablokuj
    const isFirstElement = state.elements.length === 0
    if (isFirstElement && isLoadingFirstElement) {
      console.log('handleAddPost: Pierwszy element już się ładuje, pomijam kliknięcie')
      return
    }
    
    // 🚀 Ustaw stan loading dla pierwszego elementu (optymalizacja ładowania)
    if (isFirstElement) {
      console.log('🚀🚀🚀 USTAWIAM isLoadingFirstElement = true - PRZYCIŚKI POWINNY BYĆ ZABLOKOWANE 🚀🚀🚀')
      setIsLoadingFirstElement(true)
    }
    
    const fenceConfig = fullFenceConfig

    console.log('🔍 DEBUG: handleAddPost - przed getNextElementPosition')
    
    // 🎯 NOWE: Pobierz pozycję i rotację z 3D sceny
    let position: { x: number; y: number; z: number }
    let previousElementRotation = 0
    
    if ((window as any).getNextElementPosition) {
      console.log('✅ getNextElementPosition jest dostępna w window!')
      try {
        console.log('🔄 Wywołuję getNextElementPosition...')
        console.log(`🔍 DEBUG handleAddPost: selectedVariant=${selectedVariant}, fenceConfig?.id=${fenceConfig?.id}`)
        const result = (window as any).getNextElementPosition(fenceConfig, state.elements, 'post', selectedVariant)
        console.log('📋 Wynik getNextElementPosition:', result)
        if (result && result.position) {
          position = result.position
          previousElementRotation = result.rotation || 0
          DEBUG_LOGS && console.log(`🎯 handleAddPost: Otrzymano z 3D - pozycja:`, position, `rotacja: ${previousElementRotation}°`)
        } else {
          position = getNextPosition('post')
          DEBUG_LOGS && console.log(`⚠️ handleAddPost: Używam fallback - pozycja:`, position)
        }
      } catch (error) {
        console.error('❌ Błąd getNextElementPosition:', error)
        position = getNextPosition('post')
      }
    } else {
      console.log('❌ getNextElementPosition NIE jest dostępna w window!')
      position = getNextPosition('post')
    }
    
    DEBUG_LOGS && console.log(`🏗️ Dodaję słupek o wysokości: ${postHeight} bloków na pozycji:`, position)
    DEBUG_LOGS && console.log(`🎯 handleAddPost: Rotacja poprzedniego elementu: ${previousElementRotation}°`)
    
    // 🎯 KLUCZOWA ZMIANA: Najpierw dodaj element do 3D żeby otrzymać rzeczywiste wymiary
    if ((window as any).addFenceElement) {
      try {
        // Tymczasowe ID dla elementu 3D
        const tempPostId = `temp-post-${Date.now()}`
        
        // 🎯 DODAJ SŁUPEK DO 3D I OTRZYMAJ RZECZYWISTE WYMIARY
        DEBUG_LOGS && console.log(`🔍 DEBUG ElementToolbar: fenceConfig:`, fenceConfig)
        DEBUG_LOGS && console.log(`🔍 DEBUG ElementToolbar: fenceConfig.elements:`, fenceConfig?.elements)
        // 🎯 postHeight to liczba zwykłych bloków (z suwaka), ale addFenceElement oczekuje całkowitej wysokości (zwykłe + dekoracyjne)
        const decorationBlocksCount = fenceConfig?.elements?.post?.decorationBlocksCount || 1
        const totalHeight = postHeight + decorationBlocksCount
        DEBUG_LOGS && console.log(`🎯 ElementToolbar: postHeight (zwykłe bloki)=${postHeight}, decorationBlocksCount=${decorationBlocksCount}, totalHeight=${totalHeight}`)
        DEBUG_LOGS && console.log(`🎯 DEBUG ElementToolbar: Wywołuję addFenceElement z tempPostId: ${tempPostId}`)
        await (window as any).addFenceElement(
          tempPostId,
          'post',
          position,
          fenceConfig,
          totalHeight,
          state.selectedColor,
          async (
            receivedElementId: string,
            position: { x: number; y: number; z: number },
            dimensions: { width: number; height: number }
          ) => {
            DEBUG_LOGS && console.log(`🔄 Callback z 3D WYWOŁANY: tempPostId=${tempPostId}, receivedElementId=${receivedElementId}`)
            DEBUG_LOGS && console.log(`🔄 Callback z 3D: otrzymałem rzeczywiste wymiary słupka:`, dimensions)

            // 🎯 KLUCZOWE: wysokość słupka w BLOKACH bierzemy z totalHeight (postHeight + decorationBlocksCount),
            // a nie z dimensions.height z 3D, żeby np. Slim miał 11+1 bloków.
            const dimensionsWithBlocks = {
              ...dimensions,
              height: totalHeight
            }

            // 🎯 TERAZ DODAJ SŁUPEK DO REACT Z RZECZYWISTĄ SZEROKOŚCIĄ I WYSOKOŚCIĄ W BLOKACH
            const postElementId = await addPost(position, dimensionsWithBlocks, isFullFence ? selectedVariant : undefined)
            DEBUG_LOGS && console.log(`✅ Słupek dodany do React z ID: ${postElementId} i rzeczywistymi wymiarami:`, dimensions)
            
            // 🎯 USUNIĘTE: Nie aktualizuj rotation w React state - zostanie 0° dla suwaka
            // Orientacja wizualna jest dziedziczona w ThreeJSCanvas3D.tsx
            DEBUG_LOGS && console.log(`🎯 Nowy słupek: rotation w React state = 0° (suwak na środku), orientacja wizualna dziedziczona w 3D`)
            
            // Zaktualizuj ID elementu 3D
            DEBUG_LOGS && console.log(`🔍 handleAddPost: Sprawdzam czy updateElementId jest dostępny:`, !!(window as any).updateElementId)
            if ((window as any).updateElementId && typeof (window as any).updateElementId === 'function') {
              DEBUG_LOGS && console.log(`🔄 handleAddPost: Wywołuję updateElementId(${tempPostId}, ${postElementId})`)
              try {
                (window as any).updateElementId(tempPostId, postElementId)
                DEBUG_LOGS && console.log(`✅ updateElementId wykonane`)
                
                // 🎯 NOWE: Synchronizuj rotację PO updateElementId (gdy element ma już prawidłowy ID w 3D)
                if (previousElementRotation !== 0 && (window as any).syncElementRotation) {
                  DEBUG_LOGS && console.log(`🔄 handleAddPost: Synchronizuję rotację PO updateElementId: ${previousElementRotation}°`)
                  try {
                    (window as any).syncElementRotation(postElementId, previousElementRotation)
                    DEBUG_LOGS && console.log(`✅ Rotacja zsynchronizowana z 3D sceną PO updateElementId`)
                  } catch (error) {
                    console.error(`❌ Błąd podczas synchronizacji rotacji PO updateElementId:`, error)
                  }
                }
              } catch (error) {
                console.error(`❌ Błąd podczas updateElementId:`, error)
              }
            } else {
              console.warn(`⚠️ handleAddPost: updateElementId nie jest dostępny lub nie jest funkcją!`)
            }
            
            // 🚀 Odblokuj przyciski po załadowaniu pierwszego elementu
            if (isFirstElement) {
              setIsLoadingFirstElement(false)
            }
          },
          undefined, // widthMultiplier (nie dotyczy słupka)
          isFullFence ? selectedVariant : undefined // 🎯 Przekaż variant dla "Pełne"
        )
        
      } catch (error) {
        console.error('❌ Błąd dodawania elementu 3D:', error)
        // Fallback: dodaj element do React z domyślnymi wymiarami
        const elementId = await addPost(position, undefined, isFullFence ? selectedVariant : undefined)
        DEBUG_LOGS && console.log(`✅ Element dodany do React (fallback):`, elementId)
        
        // 🚀 Odblokuj przyciski po błędzie
        if (isFirstElement) {
          setIsLoadingFirstElement(false)
        }
      }
    } else {
      // Fallback: dodaj element do React z domyślnymi wymiarami
      const elementId = await addPost(position, undefined, isFullFence ? selectedVariant : undefined)
      DEBUG_LOGS && console.log(`✅ Element dodany do React (fallback - brak 3D):`, elementId)
      
      // 🚀 Odblokuj przyciski po fallbacku
      if (isFirstElement) {
        setIsLoadingFirstElement(false)
      }
    }


    onElementSelect(null)
  }

  const handleAddGate = async () => {
    // 🚫 BLOKADA: Nie można dodawać elementów po zamknięciu obwodu
    if (state.isClosed) {
      console.log('❌ handleAddGate: Obwód jest zamknięty - nie można dodawać elementów!')
      alert('Nie można dodawać elementów po zamknięciu obwodu!')
      return
    }
    
    // 🎯 FIX: Używaj tylko fullFenceConfig (FenceConfig), nie selectedFence (FenceSystem)
    if (!fullFenceConfig) {
      alert('Najpierw wybierz system ogrodzenia i poczekaj na załadowanie konfiguracji!')
      return
    }
    
    const fenceConfig = fullFenceConfig

    // 🚪 Gate zawsze ma widthMultiplier = 2
    const widthMultiplier = 2 // Brama domyślnie 2x
    
    // 🎯 Ustaw __nextElementWidthMultiplier PRZED wywołaniem getNextElementPosition
    // żeby rotationManager.ts mógł go użyć do obliczenia offsetu od lewej
    ;(window as any).__nextElementWidthMultiplier = widthMultiplier

    // 🎯 NOWE: Pobierz pozycję i rotację z 3D sceny
    let position: { x: number; y: number; z: number }
    let previousElementRotation = 0
    
    if ((window as any).getNextElementPosition) {
      try {
        const result = (window as any).getNextElementPosition(fenceConfig, state.elements, 'gate', selectedVariant)
        if (result && result.position) {
          position = result.position
          previousElementRotation = result.rotation || 0
          DEBUG_LOGS && console.log(`🎯 handleAddGate: Otrzymano z 3D - pozycja:`, position, `rotacja: ${previousElementRotation}°`)
        } else {
          position = getNextPosition('gate')
          DEBUG_LOGS && console.log(`⚠️ handleAddGate: Używam fallback - pozycja:`, position)
        }
      } catch (error) {
        console.error('❌ Błąd getNextElementPosition:', error)
        position = getNextPosition('gate')
      }
    } else {
      position = getNextPosition('gate')
    }
    
    DEBUG_LOGS && console.log(`🎯 handleAddGate: Rotacja poprzedniego elementu: ${previousElementRotation}°`)
    
    // Dodaj element do kontekstu React (Canvas2D) i otrzymaj jego ID
    const elementId = await addGate(position, undefined, isFullFence ? selectedVariant : undefined)
    DEBUG_LOGS && console.log(`✅ Element dodany do React z ID: ${elementId}`)
    
    // 🎯 USUNIĘTE: Nie aktualizuj rotation w React state - zostanie 0° dla suwaka
    // Orientacja wizualna jest dziedziczona w ThreeJSCanvas3D.tsx
    DEBUG_LOGS && console.log(`🎯 Nowa brama: rotation w React state = 0° (suwak na środku), orientacja wizualna dziedziczona w 3D`)

    // Dodaj element do sceny 3D (jeśli funkcja jest dostępna)
    if ((window as any).addFenceElement) {
      try {
        await (window as any).addFenceElement(
          elementId, 
          'gate', 
          position, 
          fenceConfig, 
          undefined, // postHeight
          state.selectedColor, 
          // Callback żeby dodać element do kontekstu React (Canvas2D)
          (receivedElementId: string, _elementType: string, position: { x: number; y: number; z: number }, dimensions: { width: number; height: number }) => {
            DEBUG_LOGS && console.log(`🔄 Callback z 3D: aktualizuję element ${receivedElementId}`, { position, dimensions })
            
            // Zaktualizuj wymiary elementu z rzeczywistymi wartościami z DAE
            updateElement(receivedElementId, {
              width: dimensions.width,
              height: dimensions.height
            })
            DEBUG_LOGS && console.log(`📏 Zaktualizowano wymiary elementu ${receivedElementId}:`, dimensions)
            
            // 🎯 NOWE: Synchronizuj rotację z 3D sceną
            if (previousElementRotation !== 0 && (window as any).syncElementRotation) {
              try {
                (window as any).syncElementRotation(receivedElementId, previousElementRotation)
                DEBUG_LOGS && console.log(`✅ Rotacja bramy zsynchronizowana z 3D sceną`)
              } catch (error) {
                console.error(`❌ Błąd podczas synchronizacji rotacji bramy:`, error)
              }
            }
          },
          widthMultiplier, // 🚪 Przekaż widthMultiplier
          selectedVariant // 🎯 Przekaż variant (dla Royal Mix / Muro Mix również!)
        )
      } catch (error) {
        console.error('❌ Błąd dodawania elementu 3D:', error)
      }
    }

    onElementSelect(null)
  }

  const handleAddDoor = async () => {
    // 🚫 BLOKADA: Nie można dodawać elementów po zamknięciu obwodu
    if (state.isClosed) {
      console.log('❌ handleAddDoor: Obwód jest zamknięty - nie można dodawać elementów!')
      alert('Nie można dodawać elementów po zamknięciu obwodu!')
      return
    }
    
    const fenceConfig = fullFenceConfig || selectedFence
    if (!fenceConfig) {
      alert('Najpierw wybierz system ogrodzenia!')
      return
    }

    // 🚪 Oblicz widthMultiplier dla Royal Mix (S50/P50): domyślnie 2x szerokość dla drzwi
    // Sprawdź zarówno fenceConfig.id jak i selectedVariant
    const isRoyalMix = fenceConfig?.id === 'royal-mix' || fenceConfig?.id === 'royal_s50' || fenceConfig?.id === 'royal_p50'
    const isRoyalVariant = selectedVariant === 's50' || selectedVariant === 'p50'
    const defaultDoorWidth = (isRoyalMix || isRoyalVariant) ? 2 : 1
    
    // 🎯 Ustaw __nextElementWidthMultiplier PRZED wywołaniem getNextElementPosition
    // żeby rotationManager.ts mógł go użyć do obliczenia offsetu od lewej
    // Ustaw na undefined, żeby nie było domyślnej wartości (offset tylko dla 2x)
    ;(window as any).__nextElementWidthMultiplier = defaultDoorWidth
    
    // 🎯 NOWE: Pobierz pozycję i rotację z 3D sceny
    let position: { x: number; y: number; z: number }
    let previousElementRotation = 0
    
    if ((window as any).getNextElementPosition) {
      try {
        const result = (window as any).getNextElementPosition(fenceConfig, state.elements, 'door', selectedVariant)
        if (result && result.position) {
          position = result.position
          previousElementRotation = result.rotation || 0
          DEBUG_LOGS && console.log(`🎯 handleAddDoor: Otrzymano z 3D - pozycja:`, position, `rotacja: ${previousElementRotation}°`)
        } else {
          position = getNextPosition('door')
          DEBUG_LOGS && console.log(`⚠️ handleAddDoor: Używam fallback - pozycja:`, position)
        }
      } catch (error) {
        console.error('❌ Błąd getNextElementPosition:', error)
        position = getNextPosition('door')
      }
    } else {
      position = getNextPosition('door')
    }
    
    DEBUG_LOGS && console.log(`🎯 handleAddDoor: Rotacja poprzedniego elementu: ${previousElementRotation}°`)
    
    // Dodaj element do kontekstu React (Canvas2D) i otrzymaj jego ID
    const elementId = await addDoor(position, undefined, isFullFence ? selectedVariant : undefined)
    DEBUG_LOGS && console.log(`✅ Element dodany do React z ID: ${elementId}`)
    
    // 🎯 USUNIĘTE: Nie aktualizuj rotation w React state - zostanie 0° dla suwaka
    // Orientacja wizualna jest dziedziczona w ThreeJSCanvas3D.tsx
    DEBUG_LOGS && console.log(`🎯 Nowa furtka: rotation w React state = 0° (suwak na środku), orientacja wizualna dziedziczona w 3D`)
    // 🎯 Poczekaj na aktualizację state, a następnie sprawdź widthMultiplier z elementu
    await new Promise(resolve => setTimeout(resolve, 0)) // Pozwól na aktualizację state
    const doorElement = state.elements.find(e => e.id === elementId)
    // 🎯 Użyj widthMultiplier z elementu (które powinno być już ustawione na 2 w addDoor), lub defaultDoorWidth jako fallback
    const widthMultiplier = doorElement?.widthMultiplier ?? defaultDoorWidth
    
    // 🎯 Zaktualizuj __nextElementWidthMultiplier na rzeczywistą wartość z elementu (może być różna od defaultDoorWidth)
    ;(window as any).__nextElementWidthMultiplier = widthMultiplier
    DEBUG_LOGS && console.log(`🚪 handleAddDoor: Zaktualizowano __nextElementWidthMultiplier=${widthMultiplier} (doorElement?.widthMultiplier=${doorElement?.widthMultiplier}, defaultDoorWidth=${defaultDoorWidth})`)
    DEBUG_LOGS && console.log(`🚪 handleAddDoor: widthMultiplier=${widthMultiplier} (doorElement?.widthMultiplier=${doorElement?.widthMultiplier}, defaultDoorWidth=${defaultDoorWidth}, isRoyalMix=${isRoyalMix}, isRoyalVariant=${isRoyalVariant}, selectedVariant=${selectedVariant})`)

    // Dodaj element do sceny 3D (jeśli funkcja jest dostępna)
    if ((window as any).addFenceElement) {
      try {
        await (window as any).addFenceElement(
          elementId,
          'door',
          position,
          fenceConfig,
          undefined, // postHeight (nie dotyczy door)
          state.selectedColor,
          // Callback żeby dodać element do kontekstu React (Canvas2D)
          (receivedElementId: string, _elementType: string, position: { x: number; y: number; z: number }, dimensions: { width: number; height: number }) => {
            DEBUG_LOGS && console.log(`🔄 Callback z 3D: aktualizuję element ${receivedElementId}`, { position, dimensions })
            
            // Zaktualizuj wymiary elementu z rzeczywistymi wartościami z DAE
            updateElement(receivedElementId, {
              width: dimensions.width,
              height: dimensions.height
            })
            DEBUG_LOGS && console.log(`📏 Zaktualizowano wymiary elementu ${receivedElementId}:`, dimensions)
            
            // 🎯 NOWE: Synchronizuj rotację z 3D sceną
            if (previousElementRotation !== 0 && (window as any).syncElementRotation) {
              try {
                (window as any).syncElementRotation(receivedElementId, previousElementRotation)
                DEBUG_LOGS && console.log(`✅ Rotacja furtki zsynchronizowana z 3D sceną`)
              } catch (error) {
                console.error(`❌ Błąd podczas synchronizacji rotacji furtki:`, error)
              }
            }
          },
          widthMultiplier, // 🚪 Przekaż widthMultiplier
          selectedVariant // 🎯 Przekaż variant (dla Royal Mix / Muro Mix również!)
        )
      } catch (error) {
        console.error('❌ Błąd dodawania elementu 3D:', error)
      }
    }

    onElementSelect(null)
  }

  const handleAddSection = async () => {
    // 🚫 BLOKADA: Nie można dodawać elementów po zamknięciu obwodu
    if (state.isClosed) {
      console.log('❌ handleAddSection: Obwód jest zamknięty - nie można dodawać elementów!')
      alert('Nie można dodawać elementów po zamknięciu obwodu!')
      return
    }
    
    const fenceConfig = fullFenceConfig || selectedFence
    if (!fenceConfig) {
      alert('Najpierw wybierz system ogrodzenia!')
      return
    }

    // 🎯 NOWE: Pobierz pozycję i rotację z 3D sceny
    let position: { x: number; y: number; z: number }
    let previousElementRotation = 0
    const width = 2.0 // Domyślna szerokość sekcji
    
    if ((window as any).getNextElementPosition) {
      try {
        const result = (window as any).getNextElementPosition(fenceConfig, state.elements, 'section', selectedVariant)
        if (result && result.position) {
          position = result.position
          previousElementRotation = result.rotation || 0
          DEBUG_LOGS && console.log(`🎯 handleAddSection: Otrzymano z 3D - pozycja:`, position, `rotacja: ${previousElementRotation}°`)
        } else {
          position = getNextPosition('section')
          DEBUG_LOGS && console.log(`⚠️ handleAddSection: Używam fallback - pozycja:`, position)
        }
      } catch (error) {
        console.error('❌ Błąd getNextElementPosition:', error)
        position = getNextPosition('section')
      }
    } else {
      position = getNextPosition('section')
    }
    
    DEBUG_LOGS && console.log(`🎯 handleAddSection: Rotacja poprzedniego elementu: ${previousElementRotation}°`)
    
    // 🎯 Dla Muro P22, P38, S22, S38: domyślnie 3x szerokość (3 zestawienia obok siebie)
    // 🎯 Dla Royal S50, Royal P50, Royal Mix: domyślnie 2x szerokość (2 zestawienia obok siebie)
    // 🎯 Dla "Pełne" (lupane-muro-full) i "Mix" (lupane-muro-mix): wszystkie warianty mają 3x szerokość
    let defaultWidthMultiplier = 1
    if (fenceConfig?.id === 'lupane-muro-p22' || fenceConfig?.id === 'lupane-muro-p38' || 
        fenceConfig?.id === 'lupane-muro-s22' || fenceConfig?.id === 'lupane-muro-s38' ||
        fenceConfig?.id === 'lupane-muro-full' || fenceConfig?.id === 'lupane-muro-mix') { // 🎯 Dodaj "Pełne" i "Mix"
      defaultWidthMultiplier = 3
    } else if (fenceConfig?.id === 'royal_s50' || fenceConfig?.id === 'royal_p50' || fenceConfig?.id === 'royal-mix') {
      defaultWidthMultiplier = 2
    }
    
    // Dodaj element do kontekstu React (Canvas2D) i otrzymaj jego ID - przekaż defaultWidthMultiplier
    const elementId = await addSection(position, width, undefined, isFullFence ? selectedVariant : undefined, defaultWidthMultiplier)
    DEBUG_LOGS && console.log(`✅ Element dodany do React z ID: ${elementId}, widthMultiplier: ${defaultWidthMultiplier}`)
    
    // 🎯 USUNIĘTE: Nie aktualizuj rotation w React state - zostanie 0° dla suwaka
    // Orientacja wizualna jest dziedziczona w ThreeJSCanvas3D.tsx
    DEBUG_LOGS && console.log(`🎯 Nowa sekcja: rotation w React state = 0° (suwak na środku), orientacja wizualna dziedziczona w 3D`)
    
    // Dodaj element do sceny 3D (jeśli funkcja jest dostępna)
    if ((window as any).addFenceElement) {
      try {
        await (window as any).addFenceElement(
          elementId, 
          'section', 
          position, 
          fenceConfig, 
          undefined, // postHeight
          state.selectedColor,
          // Callback żeby dodać element do kontekstu React (Canvas2D)
          (receivedElementId: string, position: { x: number; y: number; z: number }, dimensions: { width: number; height: number }) => {
            DEBUG_LOGS && console.log(`🔄 Callback z 3D: aktualizuję element ${receivedElementId}`, { position, dimensions })
            
            // 🎯 Dla sekcji: NIE aktualizuj height z 3D - zostaw wartość z React state (w blokach)
            // Dla innych elementów: aktualizuj wymiary z rzeczywistymi wartościami z DAE
            const element = state.elements.find(e => e.id === receivedElementId)
            const updates: { width: number; height?: number } = {
              width: dimensions.width
            }
            // Tylko dla bram i furtek aktualizuj height (dla sekcji i słupków zostaw wartość z state)
            if (element?.type === 'gate' || element?.type === 'door') {
              updates.height = dimensions.height
            }
            
            updateElement(receivedElementId, updates)
            DEBUG_LOGS && console.log(`📏 Zaktualizowano wymiary elementu ${receivedElementId}:`, updates)
            
            // 🎯 NOWE: Synchronizuj rotację z 3D sceną
            DEBUG_LOGS && console.log(`🧪 TEST: ElementToolbar - previousElementRotation=${previousElementRotation}°, receivedElementId=${receivedElementId}`)
            DEBUG_LOGS && console.log(`🧪 TEST: ElementToolbar - syncElementRotation dostępna:`, !!(window as any).syncElementRotation)
            if (previousElementRotation !== 0 && (window as any).syncElementRotation) {
              try {
                (window as any).syncElementRotation(receivedElementId, previousElementRotation)
                DEBUG_LOGS && console.log(`✅ Rotacja sekcji zsynchronizowana z 3D sceną`)
              } catch (error) {
                console.error(`❌ Błąd podczas synchronizacji rotacji sekcji:`, error)
              }
            } else {
              DEBUG_LOGS && console.log(`🧪 TEST: ElementToolbar - syncElementRotation NIE wywołana, previousElementRotation=${previousElementRotation}°`)
            }
          },
          defaultWidthMultiplier, // 🎯 Domyślnie 1x, ale 3x dla Muro P22
          isFullFence ? selectedVariant : undefined // 🎯 Przekaż variant dla "Pełne"
        )
      } catch (error) {
        console.error('❌ Błąd dodawania elementu 3D:', error)
      }
    }

    onElementSelect(null)
  }

  const handleClosePerimeter = async () => {
    console.log('🔄 ElementToolbar: handleClosePerimeter wywołane')
    
    if (!fullFenceConfig && !selectedFence) {
      alert('Najpierw wybierz system ogrodzenia!')
      return
    }

    const fenceConfig = fullFenceConfig || selectedFence
    
    if (!fenceConfig) {
      alert('Brak konfiguracji ogrodzenia!')
      return
    }

    if (state.elements.length < 2) {
      alert('Dodaj co najmniej 2 elementy, aby zamknąć obwód!')
      return
    }

    if ((window as any).closeFencePerimeter) {
      try {
        console.log('🔄 Wywołuję closeFencePerimeter z konfiguracją:', fenceConfig)
        console.log('🔄 selectedVariant:', selectedVariant, 'isFullFence:', isFullFence)
        const result = await (window as any).closeFencePerimeter(fenceConfig, state.selectedColor, isFullFence ? selectedVariant : undefined)
        
        if (result && result.success) {
          console.log('✅ Obwód zamknięty:', result.message)
          
          // Jeśli sekcja została dodana, dodaj ją do React state
          if (result.addedSection) {
            // 🎯 Użyj rzeczywistej szerokości z dimensions.width (już uwzględnia widthMultiplier)
            const sectionWidth = result.addedSection.dimensions.width
            
            // 🎯 NIE przekazuj height - użyj domyślnej wysokości z konfiguracji (tak jak normalna sekcja)
            // dimensions.height może być w metrach lub blokach, ale addSection oczekuje liczby bloków
            // Dla sekcji zamykającej użyjemy domyślnej wysokości z konfiguracji (tak jak dla normalnej sekcji)
            // Normalna sekcja jest dodawana z height=undefined, więc używa (decorationBlocksCount + 1) bloków
            
            console.log('💰 ElementToolbar: Dodaję sekcję zamykającą:', {
              width: sectionWidth,
              dimensionsHeight: result.addedSection.dimensions.height,
              widthMultiplier: result.addedSection.widthMultiplier,
              baseWidth: result.addedSection.baseWidth,
              note: 'Używam domyślnej wysokości z konfiguracji (jak normalna sekcja)'
            })
            
            await addSection(
              result.addedSection.position,
              sectionWidth,
              undefined, // 🎯 NIE przekazuj height - użyj domyślnej wysokości z konfiguracji
              isFullFence ? selectedVariant : undefined,
              result.addedSection.widthMultiplier, // 🎯 Przekaż widthMultiplier do obliczenia ceny
              true // 🎯 Oznacz jako sekcję zamykającą
            )
            
            console.log('✅ Sekcja domykająca dodana do React state z widthMultiplier:', result.addedSection.widthMultiplier)
          }
          
          // 🚫 Ustaw isClosed na true w kontekście PO dodaniu sekcji - to zablokuje dodawanie elementów
          closePerimeter()
          console.log('✅ Ustawiono isClosed = true w kontekście')
        } else {
          console.error('❌ Błąd zamykania obwodu:', result?.message || 'Nieznany błąd')
          alert(result?.message || 'Nie udało się zamknąć obwodu')
        }
      } catch (error) {
        console.error('❌ Błąd w handleClosePerimeter:', error)
        alert(`Błąd zamykania obwodu: ${error instanceof Error ? error.message : 'Nieznany błąd'}`)
      }
    } else {
      console.error('❌ closeFencePerimeter nie jest dostępna w window')
      alert('Funkcja zamykania obwodu nie jest dostępna. Odśwież stronę i spróbuj ponownie.')
    }
  }

  const handleReset = () => {
    console.log('🔄 ElementToolbar: handleReset wywołane - przeładowuję stronę')
    // Najprostsze i najbardziej niezawodne rozwiązanie - przeładuj stronę
    window.location.reload()
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">
        Narzędzia
      </h3>
      
      {/* Status systemu - Kompaktowo */}
      {selectedFence && (
        <div className="mb-3 p-3 bg-white border border-gray-100 rounded-lg shadow-sm">
          <div className="space-y-1">
            <h3 className="font-sans font-semibold text-gray-900 text-sm">Ogrodzenie: {selectedFence.name}</h3>
            <p className="font-sans text-gray-700 text-xs">Możesz dodawać elementy</p>
          </div>
        </div>
      )}

      {/* 🎯 Selektor wariantu dla "Pełne" ogrodzeń */}
      {isFullFence && availableVariants.length > 0 && (
        <div className="mb-3 p-4 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
          <label className="block font-sans font-semibold text-gray-900 text-sm mb-2">
            Wybierz wariant:
          </label>
          <select
            value={selectedVariant}
            onChange={(e) => {
              const newVariant = e.target.value
              selectVariant(newVariant) // 🎯 Użyj selectVariant z kontekstu (automatycznie przeliczy ceny)
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm font-sans text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {availableVariants.map((variant: string) => (
              <option key={variant} value={variant}>
                {variant.toUpperCase()} 
                {variant === 'p22' && '(38×22×16)'}
                {variant === 'p38' && '(38×38×16)'}
                {variant === 's22' && '((12+26)×22×16)'}
                {variant === 's38' && '((12+26)×38×16)'}
                {variant === 's50' && '(50×40×20)'}
                {variant === 'p50' && '(50×20×20)'}
              </option>
            ))}
          </select>
          <p className="mt-2 font-sans text-xs text-gray-600">
            Wybrany wariant będzie używany dla nowo dodawanych elementów
          </p>
        </div>
      )}

      {/* Przyciski narzędzi - Brandbook Design */}
      <div className="space-y-3 mb-4">
        {/* Główne przyciski - 2 kolumny */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleAddPost}
            disabled={isPostDisabled}
            className="p-4 border border-gray-200 rounded-lg transition-all bg-white hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm touch-manipulation mobile-button"
            title={state.isClosed ? "Nie można dodawać elementów po zamknięciu obwodu" : isLoadingFirstElement ? "Ładowanie pierwszego elementu..." : "Dodaj słupek"}
          >
            <div className="flex flex-col items-center space-y-1">
              {isLoadingFirstElement ? (
                <span className="font-sans font-medium text-gray-500 text-sm">Ładowanie...</span>
              ) : (
                <span className="font-sans font-medium text-gray-900 text-sm">Słupek</span>
              )}
            </div>
          </button>

          <button
            onClick={handleAddSection}
            disabled={isSectionDisabled || isLoadingFirstElement}
            className={`p-4 border rounded-lg transition-all touch-manipulation mobile-button ${
              isAddingSection 
                ? 'bg-gray-100 border-gray-300 hover:bg-gray-200' 
                : 'bg-white border-gray-200 hover:bg-gray-50'
            } disabled:opacity-50 disabled:cursor-not-allowed shadow-sm`}
            title={state.isClosed ? "Nie można dodawać elementów po zamknięciu obwodu" : isLoadingFirstElement ? "Ładowanie pierwszego elementu..." : isAddingSection ? "Kliknij aby dodać przęsło" : isSectionDisabled ? (lastElementType === 'section' ? "Nie można dodać przęsła po przęśle" : "Nie można dodać przęsła po bramie lub furtce") : "Dodaj przęsło"}
          >
            <div className="flex flex-col items-center space-y-1">
              <span className="font-sans font-medium text-gray-900 text-sm">
                {isLoadingFirstElement ? 'Ładowanie...' : isAddingSection ? 'Dodaj' : 'Przęsło'}
              </span>
            </div>
          </button>
        </div>
        
        {/* Wysokość słupka - Brandbook Design */}
        <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
          <label className="block font-sans font-semibold text-gray-900 text-sm mb-2">
            Wysokość słupka (bloki):
          </label>
          <div className="flex items-center space-x-3 mb-2">
            <input
              type="range"
              min={selectedFence?.id === 'slim' || selectedFence?.id === 'slim-3d' ? 6 : 3}
              max={selectedFence?.id === 'slim' || selectedFence?.id === 'slim-3d' ? 15 : 8}
              step="1"
              value={postHeight}
              onChange={(e) => setPostHeight(parseInt(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer mobile-input"
            />
            <span className="font-sans font-bold text-gray-900 text-lg min-w-[2rem] text-center">
              {postHeight}
            </span>
          </div>
          <div className="font-sans text-gray-700 text-sm">
            {postHeight === 3 && 'Niski (3 bloki)'}
            {postHeight === 4 && 'Średni (4 bloki)'}
            {postHeight === 5 && 'Wysoki (5 bloki)'}
            {postHeight === 6 && 'Bardzo wysoki (6 bloków)'}
            {postHeight === 7 && 'Ekstra wysoki (7 bloków)'}
            {postHeight === 8 && 'Maksymalny (8 bloków)'}
            {postHeight >= 9 && postHeight <= 15 && `Wysoki (${postHeight} bloków)`}
          </div>
        </div>

        {/* Dodatkowe przyciski - 2 kolumny */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleAddGate}
            disabled={isGateDisabled || isLoadingFirstElement}
            className="p-4 border border-gray-200 rounded-lg transition-all bg-white hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm touch-manipulation mobile-button"
            title={state.isClosed ? "Nie można dodawać elementów po zamknięciu obwodu" : isLoadingFirstElement ? "Ładowanie pierwszego elementu..." : isGateDisabled ? (lastElementType === 'section' ? "Nie można dodać bramy po przęśle" : "Najpierw wybierz system ogrodzenia") : "Dodaj bramę"}
          >
            <div className="flex flex-col items-center space-y-1">
              <span className="font-sans font-medium text-gray-900 text-sm">{isLoadingFirstElement ? 'Ładowanie...' : 'Brama'}</span>
            </div>
          </button>

          <button
            onClick={handleAddDoor}
            disabled={isDoorDisabled || isLoadingFirstElement}
            className="p-4 border border-gray-200 rounded-lg transition-all bg-white hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm touch-manipulation mobile-button"
            title={state.isClosed ? "Nie można dodawać elementów po zamknięciu obwodu" : isLoadingFirstElement ? "Ładowanie pierwszego elementu..." : isDoorDisabled ? (lastElementType === 'section' ? "Nie można dodać furtki po przęśle" : "Najpierw wybierz system ogrodzenia") : "Dodaj furtkę"}
          >
            <div className="flex flex-col items-center space-y-1">
              <span className="font-sans font-medium text-gray-900 text-sm">{isLoadingFirstElement ? 'Ładowanie...' : 'Furtka'}</span>
            </div>
          </button>
        </div>
      </div>

      {/* Szerokość przęsła - Kompaktowo */}
      {isAddingSection && (
        <div className="mb-3 p-2 bg-gray-50 border border-gray-200 rounded">
          <label className="block text-xs font-medium text-gray-800 mb-1">
            Szerokość przęsła (m):
          </label>
          <input
            type="number"
            min="0.5"
            max="10"
            step="0.1"
            value={sectionWidth}
            onChange={(e) => setSectionWidth(parseFloat(e.target.value))}
            className="w-full px-2 py-1 text-xs border border-gray-200 rounded mobile-input"
          />
        </div>
      )}

      {/* Przycisk zamknij obwód */}
      <button
        onClick={handleClosePerimeter}
        disabled={!selectedFence || state.elements.length < 2 || state.isClosed}
        className="w-full p-2 bg-gray-50 border border-gray-200 rounded text-gray-700 hover:bg-gray-100 transition-colors text-xs touch-manipulation mobile-button disabled:opacity-50 disabled:cursor-not-allowed mb-2"
        title={state.isClosed ? "Obwód jest już zamknięty" : !selectedFence ? "Najpierw wybierz system ogrodzenia" : state.elements.length < 2 ? "Dodaj co najmniej 2 elementy, aby zamknąć obwód" : "Zamknij obwód ogrodzenia"}
      >
        Zamknij obwód
      </button>

      {/* Przycisk reset - Kompaktowo */}
      <button
        onClick={handleReset}
        className="w-full p-2 bg-red-50 border border-red-200 rounded text-red-700 hover:bg-red-100 transition-colors text-xs touch-manipulation mobile-button font-medium"
        title="Resetuj cały projekt"
        style={{ 
          display: 'block',
          visibility: 'visible',
          opacity: 1,
          position: 'relative',
          zIndex: 10
        }}
      >
        Resetuj projekt
      </button>
    </div>
  )
}

export default ElementToolbar
